import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function NewPatientsPage() {
  const [openFaq, setOpenFaq] = useState<number>(0);

  const faqs = [
    {
      question: 'How do I register as a new patient?',
      answer: 'You can register by calling our office at (416) 555-1234, booking an appointment online, or visiting us in person. Please bring your health card, government-issued ID, and a list of current medications to your first visit.'
    },
    {
      question: 'What should I bring to my first appointment?',
      answer: 'Please bring your valid Ontario Health Card, government-issued photo ID (driver\'s license or passport), a list of current medications and dosages, any relevant medical records from previous doctors, and information about your medical history including allergies and past surgeries.'
    },
    {
      question: 'Do you accept new patients?',
      answer: 'Yes! We are currently accepting new patients. We welcome individuals and families looking for a dedicated family doctor who provides comprehensive, personalized care.'
    },
    {
      question: 'What insurance do you accept?',
      answer: 'We accept all valid Ontario Health Insurance Plan (OHIP) cards. For services not covered by OHIP, we accept most private insurance plans. Please check with your insurance provider for specific coverage details.'
    },
    {
      question: 'How long is the first appointment?',
      answer: 'Your first appointment typically lasts 30-45 minutes. This allows time for a comprehensive health assessment, review of your medical history, discussion of any current health concerns, and development of a personalized care plan.'
    },
    {
      question: 'Can I transfer my medical records?',
      answer: 'Yes, we can help you transfer your medical records from your previous doctor. Please sign a consent form authorizing the transfer, and we will coordinate with your previous healthcare provider to obtain your records.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-emerald-500 text-white px-6 py-3 rounded-full mb-6">
              <i className="ri-checkbox-circle-fill text-xl"></i>
              <span className="font-semibold">We're Accepting New Patients</span>
            </div>
            <h1 className="font-serif text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Welcome New Patients
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We're excited to welcome you to our practice. Here's everything you need to know to get started with your healthcare journey.
            </p>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-bold text-gray-900 mb-4">
              What to Expect on Your First Visit
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Your first appointment is an important step in establishing your healthcare relationship
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '1',
                icon: 'ri-file-list-3-line',
                title: 'Registration',
                description: 'Complete new patient forms and provide your health card and identification'
              },
              {
                step: '2',
                icon: 'ri-chat-3-line',
                title: 'Health History',
                description: 'Discuss your medical history, current medications, and health concerns'
              },
              {
                step: '3',
                icon: 'ri-stethoscope-line',
                title: 'Physical Exam',
                description: 'Comprehensive physical examination and vital signs assessment'
              },
              {
                step: '4',
                icon: 'ri-heart-pulse-line',
                title: 'Care Plan',
                description: 'Develop a personalized healthcare plan tailored to your needs'
              }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border border-blue-100 shadow-lg h-full">
                  <div className="absolute -top-4 -left-4 w-12 h-12 flex items-center justify-center bg-blue-600 text-white rounded-full font-bold text-xl shadow-lg">
                    {item.step}
                  </div>
                  <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mb-6 mx-auto mt-4">
                    <i className={`${item.icon} text-3xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{item.title}</h3>
                  <p className="text-gray-600 text-center leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Bring */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-bold text-gray-900 mb-4">
              What to Bring to Your First Appointment
            </h2>
            <p className="text-xl text-gray-600">
              Please bring the following items to ensure a smooth registration process
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: 'ri-bank-card-line',
                title: 'Valid Health Card',
                description: 'Your current Ontario Health Insurance Plan (OHIP) card',
                required: true
              },
              {
                icon: 'ri-id-card-line',
                title: 'Government-Issued ID',
                description: 'Driver\'s license, passport, or other official identification',
                required: true
              },
              {
                icon: 'ri-capsule-line',
                title: 'Current Medication List',
                description: 'Names, dosages, and frequency of all medications you\'re taking',
                required: true
              },
              {
                icon: 'ri-file-text-line',
                title: 'Previous Medical Records',
                description: 'Any relevant medical history, test results, or specialist reports',
                required: false
              },
              {
                icon: 'ri-heart-pulse-line',
                title: 'Allergy Information',
                description: 'List of any known allergies to medications or substances',
                required: false
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Insurance Information',
                description: 'Private insurance cards if you have additional coverage',
                required: false
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-blue-600">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 flex items-center justify-center bg-blue-100 rounded-lg">
                      <i className={`${item.icon} text-2xl text-blue-600`}></i>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-bold text-gray-900">{item.title}</h3>
                      {item.required && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-semibold whitespace-nowrap">
                          Required
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download Forms */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-2xl p-12 text-white text-center shadow-2xl">
            <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
              <i className="ri-file-download-line text-4xl"></i>
            </div>
            <h2 className="font-serif text-3xl font-bold mb-4">
              Download New Patient Forms
            </h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Save time at your appointment by downloading and completing these forms in advance
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="#"
                className="inline-flex items-center gap-2 bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg whitespace-nowrap"
              >
                <i className="ri-file-pdf-line text-xl"></i>
                Patient Registration Form
              </a>
              <a 
                href="#"
                className="inline-flex items-center gap-2 bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg whitespace-nowrap"
              >
                <i className="ri-file-pdf-line text-xl"></i>
                Medical History Form
              </a>
            </div>
            <p className="text-sm text-blue-200 mt-6">
              Forms are in PDF format. You can also complete them at the clinic.
            </p>
          </div>
        </div>
      </section>

      {/* Insurance Information */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl font-bold text-gray-900 mb-4">
              Insurance & Payment Information
            </h2>
            <p className="text-xl text-gray-600">
              We accept OHIP and most private insurance plans
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-shield-check-line text-blue-600"></i>
              Accepted Insurance
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Ontario Health Insurance Plan (OHIP)',
                'Blue Cross',
                'Manulife',
                'Sun Life Financial',
                'Great-West Life',
                'Canada Life',
                'Desjardins Insurance',
                'Green Shield Canada',
                'Industrial Alliance',
                'RBC Insurance',
                'Equitable Life',
                'SSQ Insurance'
              ].map((insurance, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <i className="ri-checkbox-circle-fill text-emerald-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">{insurance}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center">
                <i className="ri-information-line text-3xl text-blue-600"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Important Note</h4>
                <p className="text-gray-700 leading-relaxed">
                  Most services are covered by OHIP. For services not covered by OHIP (such as medical notes, forms, or cosmetic procedures), payment is due at the time of service. We accept cash, debit, and major credit cards.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Common questions from new patients
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                >
                  <h3 className="font-bold text-gray-900 text-lg pr-4">{faq.question}</h3>
                  <div className="flex-shrink-0">
                    <i className={`${openFaq === index ? 'ri-subtract-line' : 'ri-add-line'} text-2xl text-blue-600`}></i>
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed">
            Book your first appointment today and take the first step towards better health
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/#booking-section"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Book Your First Appointment
            </Link>
            <a 
              href="tel:+14165551234"
              className="bg-white hover:bg-gray-100 text-blue-900 px-8 py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Call (416) 555-1234
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
